import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { palette } from "../../styles/theme";

type ResizeHandle = "move" | "nw" | "ne" | "sw" | "se" | "rotate";
type ResizeCornerHandle = "nw" | "ne" | "sw" | "se";

interface PositionSideLike {
  name?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  rotation?: number;
}

type StartBox = {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
};

type DragPatch = {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
};

type Point = { x: number; y: number };

function getRotatedAABB(w: number, h: number, angleRad: number) {
  const cosa = Math.abs(Math.cos(angleRad));
  const sina = Math.abs(Math.sin(angleRad));
  return { width: w * cosa + h * sina, height: w * sina + h * cosa };
}

function computeMovePatch(
  startBox: StartBox,
  deltaX: number,
  deltaY: number
): DragPatch {
  return {
    x: startBox.x + deltaX,
    y: startBox.y + deltaY,
    w: startBox.w,
    h: startBox.h,
  };
}

function computeRotatePatch(
  startBox: StartBox,
  startPointer: Point,
  currentPointer: Point
): DragPatch {
  const startAngleRad = (startBox.rotation * Math.PI) / 180;
  const startAABB = getRotatedAABB(startBox.w, startBox.h, startAngleRad);
  const cx = startBox.x + startAABB.width / 2;
  const cy = startBox.y + startAABB.height / 2;

  const startVecAngle = Math.atan2(startPointer.y - cy, startPointer.x - cx);
  const currentVecAngle = Math.atan2(
    currentPointer.y - cy,
    currentPointer.x - cx
  );
  const deltaDeg = -((currentVecAngle - startVecAngle) * 180) / Math.PI;
  const nextRotation = startBox.rotation + deltaDeg;

  const nextAngleRad = (nextRotation * Math.PI) / 180;
  const newAABB = getRotatedAABB(startBox.w, startBox.h, nextAngleRad);

  return {
    x: cx - newAABB.width / 2,
    y: cy - newAABB.height / 2,
    w: startBox.w,
    h: startBox.h,
    rotation: nextRotation,
  };
}

function projectDeltaToLocalSize(
  mode: ResizeCornerHandle,
  deltaX: number,
  deltaY: number,
  cosa: number,
  sina: number
) {
  switch (mode) {
    case "se":
      return {
        dW: deltaX * cosa + deltaY * sina,
        dH: -deltaX * sina + deltaY * cosa,
      };
    case "ne":
      return {
        dW: deltaX * cosa + deltaY * sina,
        dH: deltaX * sina - deltaY * cosa,
      };
    case "sw":
      return {
        dW: -deltaX * cosa - deltaY * sina,
        dH: -deltaX * sina + deltaY * cosa,
      };
    case "nw":
      return {
        dW: -deltaX * cosa - deltaY * sina,
        dH: deltaX * sina - deltaY * cosa,
      };
  }
}

function getResizeAnchorOffsets(
  mode: ResizeCornerHandle,
  startBox: StartBox,
  nextW: number,
  nextH: number
): { anchor: Point; newAnchor: Point } {
  switch (mode) {
    case "se":
      return {
        anchor: { x: -startBox.w / 2, y: -startBox.h / 2 },
        newAnchor: { x: -nextW / 2, y: -nextH / 2 },
      };
    case "ne":
      return {
        anchor: { x: -startBox.w / 2, y: startBox.h / 2 },
        newAnchor: { x: -nextW / 2, y: nextH / 2 },
      };
    case "sw":
      return {
        anchor: { x: startBox.w / 2, y: -startBox.h / 2 },
        newAnchor: { x: nextW / 2, y: -nextH / 2 },
      };
    case "nw":
      return {
        anchor: { x: startBox.w / 2, y: startBox.h / 2 },
        newAnchor: { x: nextW / 2, y: nextH / 2 },
      };
  }
}

function computeResizePatch(
  mode: ResizeCornerHandle,
  startBox: StartBox,
  deltaX: number,
  deltaY: number
): DragPatch {
  const angle = (startBox.rotation * Math.PI) / 180;
  const cosa = Math.cos(angle);
  const sina = Math.sin(angle);

  const { dW, dH } = projectDeltaToLocalSize(mode, deltaX, deltaY, cosa, sina);
  const nextW = Math.max(1, startBox.w + dW);
  const nextH = Math.max(1, startBox.h + dH);

  const startAABB = getRotatedAABB(startBox.w, startBox.h, angle);
  const startCx = startBox.x + startAABB.width / 2;
  const startCy = startBox.y + startAABB.height / 2;

  const { anchor, newAnchor } = getResizeAnchorOffsets(
    mode,
    startBox,
    nextW,
    nextH
  );

  const anchorScreenX = startCx + anchor.x * cosa - anchor.y * sina;
  const anchorScreenY = startCy + anchor.x * sina + anchor.y * cosa;
  const newCx = anchorScreenX - newAnchor.x * cosa + newAnchor.y * sina;
  const newCy = anchorScreenY - newAnchor.x * sina - newAnchor.y * cosa;

  const newAABB = getRotatedAABB(nextW, nextH, angle);

  return {
    x: newCx - newAABB.width / 2,
    y: newCy - newAABB.height / 2,
    w: nextW,
    h: nextH,
  };
}

export default function PositionCanvasEditor({
  imageUrl,
  side,
  onChange,
  allowResize = true,
}: {
  imageUrl: string;
  side: PositionSideLike;
  onChange: (next: PositionSideLike) => void;
  allowResize?: boolean;
}) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{
    mode: ResizeHandle;
    pointerId: number;
    startX: number;
    startY: number;
    startBox: {
      x: number;
      y: number;
      w: number;
      h: number;
      rotation: number;
    };
  } | null>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 1, height: 1 });
  const [frameSize, setFrameSize] = useState({ width: 1, height: 1 });

  useEffect(() => {
    if (!frameRef.current) return;
    const updateFrameSize = () => {
      if (!frameRef.current) return;
      setFrameSize({
        width: frameRef.current.clientWidth || 1,
        height: frameRef.current.clientHeight || 1,
      });
    };
    updateFrameSize();
    const observer = new ResizeObserver(updateFrameSize);
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId || !frameRef.current) {
        return;
      }

      const scaleX = naturalSize.width / frameSize.width;
      const scaleY = naturalSize.height / frameSize.height;
      const deltaX = (event.clientX - drag.startX) * scaleX;
      const deltaY = (event.clientY - drag.startY) * scaleY;

      let patch: DragPatch;
      if (drag.mode === "move") {
        patch = computeMovePatch(drag.startBox, deltaX, deltaY);
      } else if (drag.mode === "rotate") {
        const rect = frameRef.current.getBoundingClientRect();
        const startPointer: Point = {
          x: (drag.startX - rect.left) * scaleX,
          y: (drag.startY - rect.top) * scaleY,
        };
        const currentPointer: Point = {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY,
        };
        patch = computeRotatePatch(drag.startBox, startPointer, currentPointer);
      } else {
        patch = computeResizePatch(drag.mode, drag.startBox, deltaX, deltaY);
      }

      const next: PositionSideLike = {
        ...side,
        x: Math.round(patch.x),
        y: Math.round(patch.y),
        w: Math.round(Math.max(1, patch.w)),
        h: Math.round(Math.max(1, patch.h)),
      };
      if (patch.rotation !== undefined) {
        next.rotation = Math.round(patch.rotation);
      }
      onChange(next);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (dragRef.current?.pointerId === event.pointerId) {
        dragRef.current = null;
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [
    frameSize.height,
    frameSize.width,
    naturalSize.height,
    naturalSize.width,
    onChange,
    side,
  ]);

  const x = side.x ?? 0;
  const y = side.y ?? 0;
  const w = side.w ?? 1;
  const h = side.h ?? 1;
  const rotation = side.rotation ?? 0;
  const scaleX = frameSize.width / naturalSize.width;
  const scaleY = frameSize.height / naturalSize.height;

  const angleRad = Math.abs((rotation * Math.PI) / 180);
  const rotatedBoxX =
    w * Math.abs(Math.cos(angleRad)) + h * Math.abs(Math.sin(angleRad));
  const rotatedBoxY =
    w * Math.abs(Math.sin(angleRad)) + h * Math.abs(Math.cos(angleRad));
  const xAdjustment = (rotatedBoxX - w) / 2;
  const yAdjustment = (rotatedBoxY - h) / 2;

  const startDrag =
    (mode: ResizeHandle) => (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dragRef.current = {
        mode,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startBox: { x, y, w, h, rotation },
      };
    };

  const handleStyle = (left: number, top: number, cursor: string) => ({
    position: "absolute" as const,
    left,
    top,
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: palette.brand.primary,
    border: "2px solid white",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
    transform: "translate(-50%, -50%)",
    cursor,
  });

  return (
    <Box>
      <Box
        ref={frameRef}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 720,
          borderRadius: "12px",
          overflow: "hidden",
          border: `1px solid ${palette.grey.lightGrey}`,
          bgcolor: "white",
          userSelect: "none",
          touchAction: "none",
        }}
      >
        <Box
          component="img"
          ref={imageRef}
          src={imageUrl}
          alt={`${side.name || "position"} preview`}
          onLoad={() => {
            if (!imageRef.current) return;
            setNaturalSize({
              width: imageRef.current.naturalWidth || 1,
              height: imageRef.current.naturalHeight || 1,
            });
            setFrameSize({
              width: imageRef.current.clientWidth || 1,
              height: imageRef.current.clientHeight || 1,
            });
          }}
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
          }}
        />

        <Box
          onPointerDown={startDrag("move")}
          sx={{
            position: "absolute",
            left: (x + xAdjustment) * scaleX,
            top: (y + yAdjustment) * scaleY,
            width: Math.max(1, w * scaleX),
            height: Math.max(1, h * scaleY),
            border: `2px solid ${palette.brand.primary}`,
            bgcolor: "rgba(0, 104, 217, 0.12)",
            cursor: "move",
            transform: `rotate(${-rotation}deg)`,
            transformOrigin: "center center",
          }}
        >
          {allowResize && (
            <>
              <Box
                onPointerDown={startDrag("nw")}
                sx={handleStyle(0, 0, "nwse-resize")}
              />
              <Box
                onPointerDown={startDrag("ne")}
                sx={handleStyle(Math.max(1, w * scaleX), 0, "nesw-resize")}
              />
              <Box
                onPointerDown={startDrag("sw")}
                sx={handleStyle(0, Math.max(1, h * scaleY), "nesw-resize")}
              />
              <Box
                onPointerDown={startDrag("se")}
                sx={handleStyle(
                  Math.max(1, w * scaleX),
                  Math.max(1, h * scaleY),
                  "nwse-resize"
                )}
              />
            </>
          )}
          <Box
            sx={{
              position: "absolute",
              left: Math.max(1, w * scaleX) / 2,
              top: -22,
              width: 0,
              height: 22,
              borderLeft: `1px dashed ${palette.brand.primary}`,
              pointerEvents: "none",
            }}
          />
          <Box
            onPointerDown={startDrag("rotate")}
            sx={handleStyle(Math.max(1, w * scaleX) / 2, -22, "grab")}
          />
        </Box>
      </Box>
    </Box>
  );
}
